import { Suspense, memo, useContext, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';

import { AnnouncementBar, Flex, toast } from '@raystack/apsara';
import { isEmpty } from 'lodash';

import AppLoader from '@src/components/AppLoader';
import { MultiChatPanel } from '@src/components/CoPilot';
import { IdentityContext } from '@src/contexts/identity/context';
import { Show } from '@src/hoc';
import { useAnnouncement, useBilling, usePendingOrderBanner } from '@src/hooks';
import { setIAMDialogState } from '@src/redux/_reducers/authReducer';
import {
  FREE_TRIAL_MESSAGE,
  FREE_TRIAL_WELCOME_TEXT
} from '@src/utils/constants/APP';
import { ROUTES } from '@src/utils/constants/Routes';

import NewSidebar from '../Sidebar/NewSidebar';

import styles from './PrivateRouteV1.module.css';

const AnnouncementBarContainer = () => {
  const { isAnnouncementAvailable, announcement } = useAnnouncement();

  return (
    <Show
      when={
        isAnnouncementAvailable &&
        !!announcement.message &&
        !!announcement.action
      }
    >
      <AnnouncementBar
        variant={announcement.type}
        text={announcement.message!}
        leadingIcon={announcement.leadingIcon}
        actionLabel={announcement.actionLabel}
        onActionClick={announcement.action!}
      />
    </Show>
  );
};

const PrivateLayout = memo(() => {
  return (
    <Flex direction='column' className={styles.wrapper}>
      <AnnouncementBarContainer />

      <Flex className={styles.main}>
        <NewSidebar />

        <div className={styles.content}>
          <Suspense fallback={<AppLoader />}>
            <Outlet /> {/* Routed content goes here */}
          </Suspense>
        </div>

        <MultiChatPanel />
      </Flex>
    </Flex>
  );
});

PrivateLayout.displayName = 'PrivateLayout';

export const PrivateRouteV1 = memo(() => {
  const { activeOrg = {}, organizations = [] } = useContext(IdentityContext);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  useBilling();
  usePendingOrderBanner();

  useEffect(() => {
    if (location?.state?.dialogPath) {
      dispatch(
        setIAMDialogState({ isOpen: true, route: location?.state?.dialogPath })
      );
      navigate(location, { state: {} });
    } else if (location?.state?.isDefaultTrialFlow) {
      toast.success(FREE_TRIAL_WELCOME_TEXT, {
        description: FREE_TRIAL_MESSAGE
      });

      navigate(location, { state: {} });
    }
  }, [location, dispatch, navigate]);

  const isOrgEnabled = useMemo(() => {
    return activeOrg?.state === 'enabled';
  }, [activeOrg?.state]);

  return isEmpty(activeOrg) && organizations.length === 0 ? (
    <Navigate replace to={`/${ROUTES.WORKSPACES}`} />
  ) : !isOrgEnabled ? (
    <Navigate replace to={`/${ROUTES.WORKSPACES}/${activeOrg.id}`} />
  ) : (
    <PrivateLayout />
  );
});

PrivateRouteV1.displayName = 'PrivateRouteV1';
